// =========================================================================
// HD-040 · Contenido de los consentimientos (fuente de verdad de la UI)
// Copia fiel al diseño aprobado docs/08_.../flujo-firma-consentimiento.html.
// Lo usan ConsentSignature (firma) y MiConsentimientoPage (consulta).
// =========================================================================
import type { ConsentType } from '@/services';

export interface ConsentItem {
  type: ConsentType;
  title: string;
  /** Etiqueta corta para la tabla de Ajustes (columna clave). */
  short: string;
  desc: string;
  /** Bloque destacado (solo calibración): explica la anonimización. */
  detail?: string;
  chips?: string[];
}

/** Obligatorios: sin ellos la app no opera (van marcados y bloqueados al firmar). */
export const REQUIRED_ITEMS: ConsentItem[] = [
  {
    type: 'terms_of_service',
    title: 'Términos de uso',
    short: 'Términos',
    desc: 'Acepto los Términos de uso del servicio.',
  },
  {
    type: 'privacy_policy',
    title: 'Política de privacidad',
    short: 'Privacidad',
    desc: 'He leído la Política de privacidad y el tratamiento de mis datos conforme al RGPD.',
  },
  {
    type: 'health_data_processing',
    title: 'Datos de salud',
    short: 'Datos de salud',
    desc: 'Autorizo tratar mis datos de salud para mi seguimiento.',
  },
  {
    type: 'image_storage',
    title: 'Guardar mis fotos',
    short: 'Guardar fotos',
    desc: 'Guardar en mi cuenta las fotos de comida (mi historial).',
  },
];

/**
 * ¿Existe de verdad el tratamiento de calibración que describía su
 * consentimiento? **No.** Y mientras no exista, no se pide.
 *
 * El texto que estuvo vivo en producción prometía tres cosas —«se quitan
 * metadatos», «se recorta al plato», «se rompe el vínculo contigo»— y al
 * comprobarlo contra el código (2026-08-01) resultó que:
 *
 *  · NO se recorta. La norma de imagen del sistema declara justo lo contrario,
 *    a propósito y por un buen motivo: recortar dejaría fuera parte del plato y
 *    falsearía el análisis (`lib/norma-imagen.ts`).
 *  · NO se rompe ningún vínculo. La foto se guarda bajo la cuenta del usuario.
 *  · NO existe corpus de calibración alguno.
 *  · «Sin metadatos» es lo único cierto, y ocurre por accidente: el navegador
 *    reescribe la imagen a WebP y en esa reescritura se pierde el EXIF. Le pasa
 *    a TODAS las fotos, se haya consentido o no, así que tampoco es un
 *    tratamiento que este consentimiento habilite.
 *
 * Y el identificador `image_calibration` no aparecía en ninguna rama de lógica:
 * concederlo o denegarlo dejaba el sistema idéntico. Un consentimiento cuya
 * retirada no produce efecto es, además, un problema con el art. 7.3 del RGPD.
 *
 * Se retira de la firma en vez de reescribirlo con palabras más suaves: no hay
 * redacción honesta para pedir permiso a alguien para algo que no se hace.
 *
 * PARA VOLVER A OFRECERLO hay que, en este orden: (1) implementar el
 * tratamiento de verdad; (2) poner esta constante a `true`; (3) escribir el
 * texto describiendo lo que el código hace, no lo que nos gustaría. La prueba
 * `consent-content.test.ts` impide saltarse el paso (1).
 *
 * El tipo NO se borra del esquema: las firmas ya registradas deben seguir
 * leyéndose. Ver `docs/08_legal_proteccion_datos/EXPEDIENTE-ASESORIA-JURIDICA.md`.
 */
export const CALIBRACION_IMPLEMENTADA = false;

/** Opcionales del hito de inscripción (premium `medical_consultation` va aparte). */
export const OPTIONAL_ITEMS: ConsentItem[] = [
  {
    type: 'research_participation',
    title: 'Investigación',
    short: 'Investigación',
    desc: 'Participar en estudios con datos seudonimizados.',
  },
  {
    type: 'marketing',
    title: 'Comunicaciones',
    short: 'Comunicaciones',
    desc: 'Recibir novedades por email.',
  },
];

export const ALL_ITEMS: ConsentItem[] = [...REQUIRED_ITEMS, ...OPTIONAL_ITEMS];

/** Paleta de marca del diseño aprobado (banda navy→azul→verde). */
export const BRAND = {
  green: '#3A9E4A',
  greenDeep: '#2DB361',
  coral: '#F26B4A',
  blue: '#2B8DD6',
  navy: '#1A2F6E',
  ink900: '#0A1330',
  ink700: '#1F2A4D',
  ink500: '#5A6680',
  ink300: '#9BA4BD',
  ink100: '#E6EAF2',
  grad: 'linear-gradient(160deg,#1A2F6E 0%,#2B8DD6 55%,#3A9E4A 100%)',
  shadowBtn: '0 6px 18px rgba(8,193,134,.32)',
} as const;
